import { displayFromCodeableConcept } from '@seen/core/codeableConcept'
import { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { recordEntries } from '@seen/core/utils'
import {
  Button,
  TypographyDetail,
  TypographyOverline,
  TypographyPUI,
} from '@seen/ui'
import { IconClipboardList } from '@tabler/icons-react'
import type { JSX } from 'react'
import { useNavigate } from 'react-router'
import { z } from 'zod'
import { InitialCarePlanStatusCombobox } from '#src/components/care-plan/InitialCarePlanStatusCombobox.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { H1Editor } from '#src/components/core/H1Editor.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { DevMedplumAppLinkOverlay } from '#src/components/medplum/DevMedplumAppLinkOverlay.tsx'

interface CarePlanHeaderProps {
  carePlan: InitialCarePlan
  activitiesPath: string
}

export function CarePlanHeader({
  carePlan,
  activitiesPath,
}: CarePlanHeaderProps): JSX.Element {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-1">
        {carePlan.isEditable ? (
          <ResourceFieldForm
            instance={carePlan}
            valueFn={(cp) => cp.title}
            patchFn={(cp, title) => {
              cp.title = title
            }}
            debounceTyping
            render={({ field }) => <H1Editor placeholder="Title" {...field} />}
            valueSchema={z
              .string()
              .min(1, { message: 'Title must be at least 1 character' })}
          />
        ) : (
          <h1>{carePlan.title}</h1>
        )}
        <DevMedplumAppLinkOverlay to={carePlan} inline />
      </div>
      <div className="flex gap-10">
        <div className="flex flex-col gap-2">
          <TypographyOverline>Status</TypographyOverline>
          <div>
            <InitialCarePlanStatusCombobox carePlan={carePlan} />
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <TypographyOverline>Type</TypographyOverline>
          <ResourceFieldForm
            instance={carePlan}
            valueFn={(cp) => cp.type}
            patchFn={(cp, t) => {
              cp.type = t
            }}
            disabled={!carePlan.isEditable}
            render={({ field }) => (
              <ComboboxButton
                options={recordEntries(InitialCarePlan.TYPES).map(
                  ([key, codeableConcept]) => ({
                    value: key,
                    label: displayFromCodeableConcept(codeableConcept) ?? '',
                  })
                )}
                variant="status"
                {...field}
              />
            )}
          />
        </div>
        {carePlan.startDate ? (
          <div className="flex flex-col gap-3">
            <TypographyOverline>Start Date</TypographyOverline>
            <TypographyPUI>
              <HoverDate date={carePlan.startDate} />
            </TypographyPUI>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <TypographyOverline>Created Date</TypographyOverline>
            <TypographyPUI>
              <HoverDate date={carePlan.createdDate} />
            </TypographyPUI>
          </div>
        )}
        {carePlan.endDate ? (
          <div className="flex flex-col gap-3">
            <TypographyOverline>End Date</TypographyOverline>
            <TypographyPUI>
              <HoverDate date={carePlan.endDate} />
            </TypographyPUI>
          </div>
        ) : null}
        <div className="flex flex-col gap-3">
          <TypographyOverline>Updated</TypographyOverline>
          <TypographyPUI>
            <HoverDate date={carePlan.lastUpdated} format="relative" />
          </TypographyPUI>
        </div>
        <div className="flex flex-col">
          <TypographyOverline>Activity</TypographyOverline>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(activitiesPath)}
          >
            <div className="flex h-5 items-center gap-1">
              <IconClipboardList className="size-4" />
              <TypographyDetail className="mt-px">
                {carePlan.notes?.length ?? 0}
              </TypographyDetail>
            </div>
          </Button>
        </div>
      </div>
    </div>
  )
}
